import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../components/shared/Layout/Layout';
import Button from '../../../components/shared/Button/Button';
import Loading from '../../../components/shared/Loading/Loading';
import StatusBadge from '../../../components/shared/StatusBadge/StatusBadge';
import {
  ReservationsContainer,
  Header,
  FiltersSection,
  FilterGroup,
  StatsCards,
  StatCard,
  ReservationsList,
  ReservationCard,
  ReservationHeader,
  ReservationDetails,
  CustomerInfo,
  ActivityInfo,
  PaymentInfo,
  ActionButtons,
  QRCode,
  EmptyState,
  Pagination
} from './Reservations.styles';

const Reservations = () => {
  const navigate = useNavigate();
  const [reservations, setReservations] = useState([]);
  const [filteredReservations, setFilteredReservations] = useState([]);
  const [filters, setFilters] = useState({
    status: 'all',
    date: '',
    activity: 'all',
    search: ''
  });
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    confirmed: 0,
    completed: 0
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadReservations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservations, filters]);

  const loadReservations = async () => {
    try {
      const queryParams = new URLSearchParams({
        ...(filters.status !== 'all' && { status: filters.status }),
        ...(filters.date && { date: filters.date }),
        ...(filters.search && { search: filters.search }),
        page: currentPage,
        limit: 10
      });

      const response = await fetch(`/api/empresa/reservations?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('empresa-token')}`
        }
      });
      const data = await response.json();
      
      if (data.success) {
        setReservations(data.reservations);
        calculateStats(data.reservations);
      } else {
        console.error('Error loading reservations:', data.error);
        setReservations([]);
      }
    } catch (error) {
      console.error('Error loading reservations:', error);
      setReservations([]);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (reservations) => {
    const stats = {
      total: reservations.length,
      pending: reservations.filter(r => r.status === 'pending').length,
      confirmed: reservations.filter(r => r.status === 'confirmed').length,
      completed: reservations.filter(r => r.status === 'completed').length
    };
    setStats(stats);
  };

  const applyFilters = () => {
    let filtered = [...reservations];

    if (filters.status !== 'all') {
      filtered = filtered.filter(r => r.status === filters.status);
    }

    if (filters.date) {
      filtered = filtered.filter(r => r.date === filters.date);
    }

    if (filters.activity !== 'all') {
      filtered = filtered.filter(r => r.activityName && r.activityName.toLowerCase().includes(filters.activity.toLowerCase()));
    }

    if (filters.search) {
      const search = filters.search.toLowerCase();
      filtered = filtered.filter(r => 
        r.customer.name.toLowerCase().includes(search) ||
        r.customer.email.toLowerCase().includes(search) ||
        r.id.toLowerCase().includes(search)
      );
    }

    // Ordenar por fecha de creación (más recientes primero)
    filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    setFilteredReservations(filtered);
    setCurrentPage(1);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getPaymentStatusText = (status) => {
    switch (status) {
      case 'held': return 'Retenido';
      case 'released': return 'Liberado';
      case 'refunded': return 'Reembolsado';
      case 'pending_payment': return 'Sin completar';
      default: return 'Pendiente';
    }
  };

  const handleScanQR = (reservation) => {
    navigate('/empresa/scanner', { 
      state: { 
        preloadedReservation: reservation 
      } 
    });
  };

  const copyQRCode = (qrCode) => {
    navigator.clipboard.writeText(qrCode);
    alert('Código QR copiado al portapapeles');
  };

  const generateQRDownload = (reservation) => {
    // Aquí iría la lógica para generar y descargar un QR como imagen
    alert(`Generando QR para reserva ${reservation.id}`);
  };

  // Paginación
  const itemsPerPage = 10;
  const totalPages = Math.ceil(filteredReservations.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedReservations = filteredReservations.slice(startIndex, startIndex + itemsPerPage);

  if (loading) {
    return (
      <Layout headerType="empresa">
        <Loading text="Cargando reservas..." />
      </Layout>
    );
  }

  return (
    <Layout headerType="empresa">
      <ReservationsContainer>
        <Header>
          <div>
            <h1>Gestión de reservas</h1>
            <p>Administra todas las reservas de tus actividades</p>
          </div>
        </Header>

        <StatsCards>
          <StatCard color="var(--primary)">
            <div className="stat-number">{stats.total}</div>
            <div className="stat-label">Total reservas</div>
          </StatCard>
          <StatCard color="var(--warning)">
            <div className="stat-number">{stats.pending}</div>
            <div className="stat-label">Pendientes</div>
          </StatCard>
          <StatCard color="var(--primary-light)">
            <div className="stat-number">{stats.confirmed}</div>
            <div className="stat-label">Confirmadas</div>
          </StatCard>
          <StatCard color="var(--success-dark)">
            <div className="stat-number">{stats.completed}</div>
            <div className="stat-label">Completadas</div>
          </StatCard>
        </StatsCards>

        <FiltersSection>
          <FilterGroup>
            <label>Estado:</label>
            <select 
              value={filters.status} 
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="all">Todos</option>
              <option value="pending">Pendientes</option>
              <option value="confirmed">Confirmadas</option>
              <option value="completed">Completadas</option>
              <option value="cancelled">Canceladas</option>
            </select>
          </FilterGroup>

          <FilterGroup>
            <label>Fecha:</label>
            <input
              type="date"
              value={filters.date}
              onChange={(e) => handleFilterChange('date', e.target.value)}
            />
          </FilterGroup>

          <FilterGroup>
            <label>Actividad:</label>
            <select 
              value={filters.activity} 
              onChange={(e) => handleFilterChange('activity', e.target.value)}
            >
              <option value="all">Todas</option>
              <option value="xcaret">Parque Acuático</option>
              <option value="cuatrimotos">Cuatrimotos</option>
              <option value="museo">Museo</option>
            </select>
          </FilterGroup>

          <FilterGroup>
            <label>Buscar:</label>
            <input
              type="text"
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              placeholder="Cliente, email o ID..."
            />
          </FilterGroup>
        </FiltersSection>

        {paginatedReservations.length > 0 ? (
          <>
            <ReservationsList>
              {paginatedReservations.map(reservation => (
                <ReservationCard key={reservation.id} status={reservation.status}>
                  <ReservationHeader>
                    <div>
                      <h3>#{reservation.id.slice(0, 8)}</h3>
                      <StatusBadge status={reservation.status} />
                    </div>
                    <div className="reservation-date">
                      {new Date(reservation.createdAt).toLocaleDateString()} {new Date(reservation.createdAt).toLocaleTimeString()}
                    </div>
                  </ReservationHeader>

                  <ReservationDetails>
                    <CustomerInfo>
                      <h4>👤 Cliente</h4>
                      <p><strong>{reservation.customer.name}</strong></p>
                      <p>{reservation.customer.email}</p>
                      <p>{reservation.customer.phone}</p>
                    </CustomerInfo>

                    <ActivityInfo>
                      <h4>🎯 Actividad</h4>
                      <p><strong>{reservation.activityName}</strong></p>
                      <p>📅 {new Date(reservation.date).toLocaleDateString()}</p>
                      <p>🕐 {reservation.time}</p>
                      <p>👥 {reservation.quantity} persona(s)</p>
                    </ActivityInfo>

                    <PaymentInfo>
                      <h4>💰 Pago</h4>
                      <p>Subtotal: ${reservation.totalAmount.toLocaleString()}</p>
                      <p>Tarifa: ${reservation.serviceFee.toLocaleString()}</p>
                      <p>IVA: ${reservation.taxes.toLocaleString()}</p>
                      <p><strong>Total: ${reservation.finalAmount.toLocaleString()}</strong></p>
                      <p className={`payment-status ${reservation.paymentStatus}`}>
                        Estado: {getPaymentStatusText(reservation.paymentStatus)}
                      </p>
                    </PaymentInfo>

                    <QRCode>
                      <h4>📱 Código QR</h4>
                      <div className="qr-actions">
                        <button onClick={() => copyQRCode(reservation.qrCode)}>
                          📋 Copiar
                        </button>
                        <button onClick={() => generateQRDownload(reservation)}>
                          ⬇️ Descargar
                        </button>
                      </div>
                      {reservation.status === 'pending' && (
                        <ActionButtons>
                          <Button 
                            size="small"
                            onClick={() => handleScanQR(reservation)}
                          >
                            📱 Escanear QR
                          </Button>
                        </ActionButtons>
                      )}
                    </QRCode>
                  </ReservationDetails>

                  {reservation.notes && (
                    <div className="reservation-notes">
                      <strong>Notas:</strong> {reservation.notes}
                    </div>
                  )}

                  {reservation.scannedAt && (
                    <div className="scan-info">
                      ✅ Escaneado el {new Date(reservation.scannedAt).toLocaleString()} por {reservation.scannedBy}
                    </div>
                  )}

                  {reservation.cancelReason && (
                    <div className="cancel-info">
                      ❌ Cancelado: {reservation.cancelReason}
                    </div>
                  )}
                </ReservationCard>
              ))}
            </ReservationsList>

            {totalPages > 1 && (
              <Pagination>
                <button 
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                >
                  ‹ Anterior
                </button>
                <span>Página {currentPage} de {totalPages}</span>
                <button 
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                >
                  Siguiente ›
                </button>
              </Pagination>
            )}
          </>
        ) : (
          <EmptyState>
            <h3>No se encontraron reservas</h3>
            <p>Ajusta los filtros o espera a que lleguen nuevas reservas</p>
          </EmptyState>
        )}
      </ReservationsContainer>
    </Layout>
  );
};

export default Reservations;
